import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LogOut, Monitor, ArrowLeft, User, CalendarPlus, Plus, Users, Share2, Link2, Link2Off, Trash2, Pencil, Copy, QrCode, Layers, ScanLine } from "lucide-react";
import ProfileDialog from "@/components/ProfileDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import QRCode from "qrcode";
import EventEditor from "@/components/events/EventEditor";
import SubmissionsView from "@/components/events/SubmissionsView";
import ScanPass from "@/components/events/ScanPass";
import { eventShareUrl, masterShareUrl, scannerShareUrl } from "@/lib/eventLib";

export default function EventsApp({ shop, onLogout, onInstall, onBack, onShopUpdate }) {
  const [showProfile, setShowProfile] = useState(false);
  const [view, setView] = useState("list");
  const [scanEvent, setScanEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [subsEvent, setSubsEvent] = useState(null);
  const [shareEvent, setShareEvent] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [masterQr, setMasterQr] = useState("");
  const [scanQr, setScanQr] = useState("");
  const [delEvent, setDelEvent] = useState(null);
  const [delMobile, setDelMobile] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/events");
      setEvents(r.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Ye feature plan lene ke baad milega");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (view === "list") load(); }, [view, load]);

  const doDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/events/${delEvent.id}`, { params: { mobile: delMobile } });
      toast.success("Event deleted");
      setDelEvent(null); setDelMobile("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete nahi hua");
    } finally { setDelBusy(false); }
  };

  const openShare = async (ev) => {
    setShareEvent(ev);
    setQrDataUrl("");
    setMasterQr("");
    setScanQr("");
    let token = ev.share_token;
    if (!ev.share_enabled && !ev.master_share_enabled) {
      const r = await api.post(`/events/${ev.id}/share`, { enabled: true });
      token = r.data.share_token;
      setShareEvent({ ...ev, share_enabled: true, share_token: token });
      load();
    }
    try { setQrDataUrl(await QRCode.toDataURL(eventShareUrl(token, ev.updated_at), { width: 320, margin: 1 })); } catch { /* noop */ }
    if (ev.master_share_enabled && ev.master_share_token) {
      try { setMasterQr(await QRCode.toDataURL(masterShareUrl(ev.master_share_token), { width: 320, margin: 1 })); } catch { /* noop */ }
    }
    if (ev.scanner_enabled && ev.scanner_token) {
      try { setScanQr(await QRCode.toDataURL(scannerShareUrl(ev.scanner_token), { width: 320, margin: 1 })); } catch { /* noop */ }
    }
  };

  const toggleMaster = async (enabled) => {
    const r = await api.post(`/events/${shareEvent.id}/master-share`, { enabled });
    const t = r.data.master_share_token;
    setShareEvent({ ...shareEvent, master_share_enabled: enabled, master_share_token: t, ...(enabled ? { share_enabled: false } : {}) });
    if (enabled) { setQrDataUrl(""); try { setMasterQr(await QRCode.toDataURL(masterShareUrl(t), { width: 320, margin: 1 })); } catch { /* noop */ } }
    else setMasterQr("");
    load();
    toast.success(enabled ? (shareEvent.share_enabled ? "Master link active — normal link band ho gayi" : "Master link active") : "Master link band kar diya");
  };

  const copyMaster = () => {
    navigator.clipboard.writeText(masterShareUrl(shareEvent.master_share_token))
      .then(() => toast.success("Master link copy ho gaya"))
      .catch(() => toast.error("Copy nahi hua"));
  };

  const toggleScanner = async (enabled) => {
    const r = await api.post(`/events/${shareEvent.id}/scanner`, { enabled });
    const t = r.data.scanner_token;
    setShareEvent({ ...shareEvent, scanner_enabled: enabled, scanner_token: t });
    if (enabled) { try { setScanQr(await QRCode.toDataURL(scannerShareUrl(t), { width: 320, margin: 1 })); } catch { /* noop */ } }
    else setScanQr("");
    load();
    toast.success(enabled ? "Scanner link active" : "Scanner link band kar diya");
  };

  const copyScanner = () => {
    navigator.clipboard.writeText(scannerShareUrl(shareEvent.scanner_token))
      .then(() => toast.success("Scanner link copy ho gaya"))
      .catch(() => toast.error("Copy nahi hua"));
  };

  const toggleShare = async (enabled) => {
    const r = await api.post(`/events/${shareEvent.id}/share`, { enabled });
    setShareEvent({ ...shareEvent, share_enabled: enabled, share_token: r.data.share_token, ...(enabled ? { master_share_enabled: false } : {}) });
    if (enabled) setMasterQr("");
    load();
    toast.success(enabled ? (shareEvent.master_share_enabled ? "Link active — master link band ho gayi" : "Link active") : "Link band kar diya");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(eventShareUrl(shareEvent.share_token, shareEvent.updated_at))
      .then(() => toast.success("Form link copy ho gaya"))
      .catch(() => toast.error("Copy nahi hua"));
  };

  if (view === "scan") {
    return <ScanPass event={scanEvent} onBack={() => { setView("list"); setScanEvent(null); }} />;
  }
  if (view === "editor") {
    return <EventEditor eventId={editId} onDone={() => { setView("list"); setEditId(null); }} />;
  }
  if (view === "subs" && subsEvent) {
    return <SubmissionsView event={subsEvent} onBack={() => { setView("list"); setSubsEvent(null); }} />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="events-app">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex">
          <div className="flex-1 bg-cyan-500" /><div className="flex-1 bg-pink-600" /><div className="flex-1 bg-yellow-400" /><div className="flex-1 bg-slate-900" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:gap-3">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="events-back-btn" className="gap-1 text-muted-foreground hover:text-foreground -ml-2">
            <ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Features</span>
          </Button>
          <img src="/logo.webp?v=2" alt="Sootrik" className="h-8 sm:h-9 w-auto" />
          <div className="w-full sm:w-auto sm:flex-1 sm:min-w-0 order-2 sm:order-none">
            <p className="font-heading font-bold text-sm sm:text-base truncate">Create Event</p>
            <p className="text-xs text-muted-foreground truncate">{shop.name}</p>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 ml-auto">
            <Button size="sm" variant="outline" onClick={onInstall} className="gap-1.5 border-cyan-500 text-cyan-700 hover:bg-cyan-50"><Monitor className="h-4 w-4" /><span className="hidden sm:inline">Install</span></Button>
            <Button size="sm" variant="ghost" onClick={() => setShowProfile(true)} data-testid="profile-btn" className="gap-1.5 text-muted-foreground hover:text-foreground"><User className="h-4 w-4" /><span className="hidden sm:inline">Profile</span></Button>
            <Button size="sm" variant="ghost" onClick={onLogout} data-testid="logout-btn" className="gap-1.5 text-muted-foreground hover:text-foreground"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Logout</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">My Events</h2>
          <div className="flex items-center gap-2">
            <Button onClick={() => { setEditId(null); setView("editor"); }} data-testid="event-new-btn" className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white">
              <Plus className="h-4 w-4" /> New Event
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Loading...</p>
        ) : events.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="events-empty">
            <CalendarPlus className="h-10 w-10 mx-auto text-slate-300" />
            <p className="text-sm text-muted-foreground mt-3">Abhi koi event nahi. "New Event" se shuru karein.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-list">
            {events.map((ev) => (
              <div key={ev.id} className="bg-card border border-border rounded-xl overflow-hidden shadow-sm" data-testid={`event-card-${ev.id.slice(0, 8)}`}>
                {ev.banner ? (
                  <img src={ev.banner} alt={ev.name} className="w-full aspect-[1200/630] object-cover" />
                ) : (
                  <div className="w-full h-28 bg-gradient-to-br from-pink-100 to-cyan-100 flex items-center justify-center"><CalendarPlus className="h-8 w-8 text-slate-400" /></div>
                )}
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-heading font-bold text-sm leading-snug">{ev.name}</p>
                    {ev.share_enabled ? <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0">LIVE</span> : <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full shrink-0">OFF</span>}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ev.submission_count} submissions{ev.pending_count > 0 ? ` · ${ev.pending_count} pending` : ""}</p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setSubsEvent(ev); setView("subs"); }} data-testid={`event-subs-btn-${ev.id.slice(0, 8)}`}><Users className="h-3 w-3" /> Submissions</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={() => { setScanEvent(ev); setView("scan"); }} data-testid={`event-scan-btn-${ev.id.slice(0, 8)}`}><QrCode className="h-3 w-3" /> Scan</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openShare(ev)} data-testid={`event-share-btn-${ev.id.slice(0, 8)}`}><Share2 className="h-3 w-3" /> Share</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-slate-600" onClick={() => { setEditId(ev.id); setView("editor"); }} data-testid={`event-edit-btn-${ev.id.slice(0, 8)}`}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-500" onClick={() => { setDelEvent(ev); setDelMobile(""); }} data-testid={`event-delete-btn-${ev.id.slice(0, 8)}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!shareEvent} onOpenChange={(v) => !v && setShareEvent(null)}>
        <DialogContent className="max-w-sm" data-testid="event-share-dialog">
          <DialogHeader><DialogTitle className="font-heading">Share Form</DialogTitle></DialogHeader>
          {shareEvent && (
            <div className="space-y-3 text-center">
              <div className="flex items-center justify-center gap-2 text-sm">
                <span>Link:</span>
                <span className={shareEvent.share_enabled ? "font-semibold text-emerald-600" : "font-semibold text-slate-400"}>{shareEvent.share_enabled ? "Active" : "Off"}</span>
              </div>
              {shareEvent.share_enabled && qrDataUrl && <img src={qrDataUrl} alt="QR" className="h-44 w-44 mx-auto border rounded-lg" data-testid="event-share-qr" />}
              {shareEvent.share_enabled && (
                <div className="flex items-center gap-2">
                  <input readOnly value={eventShareUrl(shareEvent.share_token, shareEvent.updated_at)} className="flex-1 text-xs border rounded px-2 py-1.5 bg-slate-50 truncate" data-testid="event-share-url" />
                  <Button size="sm" variant="outline" onClick={copyLink} data-testid="event-share-copy-btn" className="gap-1"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              )}
              {shareEvent.share_enabled ? (
                <Button variant="outline" className="w-full gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={() => toggleShare(false)} data-testid="event-share-disable-btn"><Link2Off className="h-4 w-4" /> Link band karo</Button>
              ) : (
                <Button className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => toggleShare(true)} data-testid="event-share-enable-btn"><Link2 className="h-4 w-4" /> Link active karo</Button>
              )}

              <div className="pt-3 mt-1 border-t text-left space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><Layers className="h-4 w-4 text-pink-600" /> Master Link</p>
                    <p className="text-[11px] text-muted-foreground">Private bulk link — active karte hi normal link band ho jayegi aur event home page se hat jayega. Sirf jise link bhejein wahi entries karega.</p>
                  </div>
                  <span className={shareEvent.master_share_enabled ? "text-[11px] font-bold text-emerald-600" : "text-[11px] font-bold text-slate-400"}>{shareEvent.master_share_enabled ? "Active" : "Off"}</span>
                </div>
                {shareEvent.master_share_enabled && masterQr && <img src={masterQr} alt="Master QR" className="h-36 w-36 mx-auto border rounded-lg" data-testid="event-master-qr" />}
                {shareEvent.master_share_enabled && (
                  <div className="flex items-center gap-2">
                    <input readOnly value={masterShareUrl(shareEvent.master_share_token)} className="flex-1 text-xs border rounded px-2 py-1.5 bg-slate-50 truncate" data-testid="event-master-url" />
                    <Button size="sm" variant="outline" onClick={copyMaster} data-testid="event-master-copy-btn" className="gap-1"><Copy className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
                {shareEvent.master_share_enabled ? (
                  <Button variant="outline" className="w-full gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={() => toggleMaster(false)} data-testid="event-master-disable-btn"><Link2Off className="h-4 w-4" /> Master link band karo</Button>
                ) : (
                  <Button className="w-full gap-1.5 bg-pink-600 hover:bg-pink-700 text-white" onClick={() => toggleMaster(true)} data-testid="event-master-enable-btn"><Link2 className="h-4 w-4" /> Master link active karo</Button>
                )}
              </div>

              <div className="pt-3 mt-1 border-t text-left space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><ScanLine className="h-4 w-4 text-cyan-600" /> Scanner Link</p>
                    <p className="text-[11px] text-muted-foreground">Gate staff ko ye link bhejein — bina ID/password ke sirf is event ke pass scan kar payenge.</p>
                  </div>
                  <span className={shareEvent.scanner_enabled ? "text-[11px] font-bold text-emerald-600" : "text-[11px] font-bold text-slate-400"}>{shareEvent.scanner_enabled ? "Active" : "Off"}</span>
                </div>
                {shareEvent.scanner_enabled && scanQr && <img src={scanQr} alt="Scanner QR" className="h-36 w-36 mx-auto border rounded-lg" data-testid="event-scanner-qr" />}
                {shareEvent.scanner_token && (
                  <div className="flex items-center gap-2">
                    <input readOnly value={scannerShareUrl(shareEvent.scanner_token)} className={`flex-1 text-xs border rounded px-2 py-1.5 truncate ${shareEvent.scanner_enabled ? "bg-slate-50" : "bg-slate-100 text-slate-400"}`} data-testid="event-scanner-url" />
                    <Button size="sm" variant="outline" onClick={copyScanner} data-testid="event-scanner-copy-btn" className="gap-1"><Copy className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
                {shareEvent.scanner_token && !shareEvent.scanner_enabled && (
                  <p className="text-[11px] text-amber-600" data-testid="event-scanner-inactive-note">Link abhi inactive hai — scan kaam nahi karega. Link copy kar sakte hain; "Scanner link active karo" se wapas chalu ho jayegi.</p>
                )}
                {shareEvent.scanner_enabled ? (
                  <Button variant="outline" className="w-full gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={() => toggleScanner(false)} data-testid="event-scanner-disable-btn"><Link2Off className="h-4 w-4" /> Scanner link band karo</Button>
                ) : (
                  <Button className="w-full gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => toggleScanner(true)} data-testid="event-scanner-enable-btn"><Link2 className="h-4 w-4" /> Scanner link active karo</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProfileDialog open={showProfile} onClose={() => setShowProfile(false)} shop={shop} onUpdated={onShopUpdate} />
      <Dialog open={!!delEvent} onOpenChange={(v) => !v && setDelEvent(null)}>
        <DialogContent className="max-w-sm" data-testid="event-delete-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Are you sure?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">"{delEvent?.name}" aur iski saari submissions permanently delete ho jayengi. Confirm karne ke liye apna <b>registered mobile number</b> daalein.</p>
          <Input value={delMobile} onChange={(e) => setDelMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="Registered mobile number" data-testid="event-delete-mobile" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setDelEvent(null)} data-testid="event-delete-cancel">Cancel</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={delBusy || delMobile.length < 10} onClick={doDelete} data-testid="event-delete-confirm">{delBusy ? "Deleting..." : "Delete"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
